import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export { cancelEvent, stopPropagation } from "./cancelEvent";
export { parameterAwareDebounce } from "./parameterAwareDebounce";
export {
  formatFileSize,
  formatDuration,
  formatTimestamp,
  formatDate,
  formatDateISO,
  formatTime,
} from "./formatters";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

let isMac = false;

if (typeof navigator !== "undefined") {
  const { platform } = navigator;
  if (
    platform?.startsWith("Mac") ||
    platform?.startsWith("iPhone") ||
    platform?.startsWith("iPad")
  ) {
    isMac = true;
  }
}

export { isMac };

// Generic status color mapping using semantic colors
const defaultStatusColors = {
  ready: "bg-success",
  failed: "bg-destructive",
  connecting: "bg-primary",
  authenticating: "bg-warning",
  discovering: "bg-secondary",
  success: "bg-success",
  error: "bg-destructive",
  pending: "bg-primary",
  warning: "bg-warning",
  info: "bg-info",
} as const;

// Text color mappings for status indicators
const defaultStatusTextColors = {
  ok: "text-success",
  error: "text-destructive",
  warning: "text-warning",
  info: "text-info",
  success: "text-success",
  failed: "text-destructive",
  ready: "text-success",
  pending: "text-primary",
  connecting: "text-primary",
  authenticating: "text-warning",
  discovering: "text-secondary",
} as const;

/**
 * Convert a status to a Tailwind background color class using semantic colors
 *
 * @param status - The status to convert
 * @param colorMap - Optional custom color mapping, defaults to standard status colors
 * @returns The Tailwind background color class
 */
export function getStatusColor<T extends string>(
  status: T,
  colorMap?: Record<T, string>,
): string {
  const colors = colorMap || (defaultStatusColors as Record<T, string>);
  return colors[status] || "bg-muted";
}

/**
 * Convert a status to a Tailwind text color class using semantic colors
 *
 * @param status - The status to convert
 * @param colorMap - Optional custom color mapping, defaults to standard text colors
 * @returns The Tailwind text color class
 */
export function getStatusTextColor<T extends string>(
  status: T,
  colorMap?: Record<T, string>,
): string {
  const colors = colorMap || (defaultStatusTextColors as Record<T, string>);
  return colors[status] || "text-muted-foreground";
}
